// ============================================================================
// G-004 — KES OSINT Actor Abstraction Layer (Sprint 006 + Prompt 4)
// ============================================================================
// Single source of truth untuk Apify actor IDs + per-platform config.
// Admin bisa swap actor ID via env var TANPA code change.
//
// Env vars (optional override):
//   VITE_ACTOR_SEARCH_<PLATFORM>   — Stage 1 actor (e.g. INSTAGRAM, FACEBOOK)
//   VITE_ACTOR_SCRAPE_<PLATFORM>   — Stage 2 actor
//
// Verified Apify Org Scale plan ($199/mo) — 2026-05-05.
// ============================================================================

const E = (typeof import.meta !== 'undefined' ? import.meta.env : {}) || {};

// Helper: env-driven actor with default fallback
const ACT = (envKey, defaultActor) => E[envKey] || defaultActor;

// ============================================================================
// STAGE 1 — Netcomm The Radar (Discovery)
// ============================================================================
// Value: string OR array (multi-actor dispatch parallel + merge + de-dup).
//
// Facebook dual-actor (per Andra request):
//   1. apify~facebook-pages-scraper  — Pages (business) + sebagian Personal
//   2. crowdpull~facebook-profile-scraper — Personal accounts publik (no login)
// ============================================================================
export const STAGE1_ACTOR_MAP = {
  // SHARPENING 2026-05-19 (user spec — Apify actor table):
  //   Twitter/X: apidojo~tweet-scraper            (searchTerms + twitterHandles)
  //   Instagram: apify~instagram-scraper          (hashtag + user_posts)
  //   TikTok:    clockworks~tiktok-scraper        (search + hashtag + profile fan-out)
  //   YouTube:   streamers~youtube-scraper        (search + channel)
  //   Facebook ⚡: alien_force~facebook-search-scraper + apify~facebook-posts-scraper
  //   Threads:   igview-owner~threads-search-scraper  (keyword search)
  //   Reddit:    trudax~reddit-scraper-lite       (search subreddit/user)
  //   LinkedIn:  apify~google-search-scraper      (site:linkedin.com dork)
  //   Web:       apify~google-search-scraper      (general SERP)
  //   News:      easyapi~google-news-scraper      (+ google-search-scraper fallback)
  instagram: ACT('VITE_ACTOR_SEARCH_INSTAGRAM', 'apify~instagram-scraper'),
  // Facebook dual-actor: search (keyword) + posts-scraper untuk enrich top hits.
  // alien_force~facebook-search-scraper → broad coverage (posts/pages/people).
  facebook:  ACT('VITE_ACTOR_SEARCH_FACEBOOK',  ['alien_force~facebook-search-scraper', 'apify~facebook-posts-scraper']),
  tiktok:    ACT('VITE_ACTOR_SEARCH_TIKTOK',   'clockworks~tiktok-scraper'),
  x:         ACT('VITE_ACTOR_SEARCH_X',        'apidojo~tweet-scraper'),
  twitter:   ACT('VITE_ACTOR_SEARCH_X',        'apidojo~tweet-scraper'),
  // LinkedIn: Google dork (skip cookie-dependent actor)
  linkedin:  ACT('VITE_ACTOR_SEARCH_LINKEDIN', 'apify~google-search-scraper'),
  // Threads: switch ke search-scraper (keyword), bukan profile-api yang req handle
  threads:   ACT('VITE_ACTOR_SEARCH_THREADS',  'igview-owner~threads-search-scraper'),
  // Reddit: new platform (sharpening 2026-05-19)
  reddit:    ACT('VITE_ACTOR_SEARCH_REDDIT',   'trudax~reddit-scraper-lite'),
  youtube:   ACT('VITE_ACTOR_SEARCH_YOUTUBE',  'streamers~youtube-scraper'),
  web:       ACT('VITE_ACTOR_SEARCH_WEB',      'apify~google-search-scraper'),
  // News: dedicated google-news-scraper (sharpening 2026-05-19 — sebelumnya
  // pakai google-search-scraper dgn site: filter). Fallback chain di ACTOR_FALLBACKS.
  news:      ACT('VITE_ACTOR_SEARCH_NEWS',     'easyapi~google-news-scraper'),
  maps:      ACT('VITE_ACTOR_SEARCH_MAPS',     'apify~google-maps-scraper'),
};

// ============================================================================
// STAGE 2 — Netcomm Digital Forensic (Enrichment)
// ============================================================================
export const STAGE2_ACTOR_MAP = {
  // SHARPENING 2026-05-19 (user spec): sync dgn Stage 1 actor table.
  // Stage 2 = deep enrichment per discovered profile (post + bio + foto detail).
  instagram: ACT('VITE_ACTOR_SCRAPE_INSTAGRAM', 'apify~instagram-scraper'),
  // Facebook combo: same as Stage 1 — search + posts scraper
  facebook:  ACT('VITE_ACTOR_SCRAPE_FACEBOOK', ['alien_force~facebook-search-scraper', 'apify~facebook-posts-scraper']),
  tiktok:    ACT('VITE_ACTOR_SCRAPE_TIKTOK',   'clockworks~tiktok-scraper'),
  x:         ACT('VITE_ACTOR_SCRAPE_X',        'apidojo~tweet-scraper'),
  twitter:   ACT('VITE_ACTOR_SCRAPE_X',        'apidojo~tweet-scraper'),
  // LinkedIn Stage 2: pakai content-crawler buat scrape profile URL hasil Google.
  // No cookie required, no HTTP 999. Data: title, headline, summary, posts.
  linkedin:  ACT('VITE_ACTOR_SCRAPE_LINKEDIN', 'apify~website-content-crawler'),
  threads:   ACT('VITE_ACTOR_SCRAPE_THREADS',  'igview-owner~threads-search-scraper'),
  reddit:    ACT('VITE_ACTOR_SCRAPE_REDDIT',   'trudax~reddit-scraper-lite'),
  youtube:   ACT('VITE_ACTOR_SCRAPE_YOUTUBE',  'streamers~youtube-scraper'),
  web:       ACT('VITE_ACTOR_SCRAPE_WEB',      'apify~website-content-crawler'),
  news:      ACT('VITE_ACTOR_SCRAPE_NEWS',     'easyapi~google-news-scraper'),
  content:   ACT('VITE_ACTOR_SCRAPE_WEB',      'apify~website-content-crawler'),
  maps:      ACT('VITE_ACTOR_SCRAPE_MAPS',     'apify~google-maps-scraper'),
};

// ============================================================================
// FALLBACK ACTORS — dipakai saat primary actor return 0 results / error
// ============================================================================
export const ACTOR_FALLBACKS = {
  x:        ACT('VITE_ACTOR_FALLBACK_X',        'apidojo~twitter-scraper-lite'),
  threads:  ACT('VITE_ACTOR_FALLBACK_THREADS',  'apify~threads-scraper'),
  linkedin: ACT('VITE_ACTOR_FALLBACK_LINKEDIN', 'dev_fusion~linkedin-profile-scraper'),
  // Sharpening 2026-05-19 — fallback untuk new primary actors:
  tiktok:   ACT('VITE_ACTOR_FALLBACK_TIKTOK',   'clockworks~tiktok-profile-scraper'),
  facebook: ACT('VITE_ACTOR_FALLBACK_FACEBOOK', 'apify~facebook-pages-scraper'),
  news:     ACT('VITE_ACTOR_FALLBACK_NEWS',     'apify~google-search-scraper'),
};

// ============================================================================
// PROXY CONFIG — Per Apify proxy group rules
// ============================================================================
// Rules (verified):
//   GOOGLE_SERP    — WAJIB untuk Google domain (Search, News, Maps)
//   RESIDENTIAL    — WAJIB untuk sosmed besar (IG/FB/TikTok/LinkedIn/Threads)
//                    Country 'ID' wajib untuk subjek Indonesia
//   auto           — Datacenter (cukup untuk YouTube + web crawler)
//   internal       — X (apidojo actor manage proxy sendiri)
// ============================================================================
export const PROXY_CONFIG = {
  instagram: { group: 'RESIDENTIAL', country: 'ID', sticky: false },
  tiktok:    { group: 'RESIDENTIAL', country: 'ID', sticky: false },
  facebook:  { group: 'RESIDENTIAL', country: 'ID', sticky: true  },  // sticky for cookies
  linkedin:  { group: 'RESIDENTIAL', country: 'ID', sticky: true  },  // sticky required
  threads:   { group: 'RESIDENTIAL', country: 'ID', sticky: false },
  reddit:    { group: 'RESIDENTIAL', country: 'ID', sticky: false },  // added 2026-05-19
  x:         { group: null,          country: null, sticky: false },  // actor-managed
  twitter:   { group: null,          country: null, sticky: false },
  youtube:   { group: 'auto',        country: null, sticky: false },
  web:       { group: 'GOOGLE_SERP', country: null, sticky: false },
  news:      { group: 'GOOGLE_SERP', country: null, sticky: false },
  content:   { group: 'auto',        country: 'ID', sticky: false },
  maps:      { group: 'GOOGLE_SERP', country: null, sticky: false },
};

// ============================================================================
// TIMEOUT — Per platform (seconds, server-side proxy timeout)
// ============================================================================
// LinkedIn slowest (cookie validation), Google Search fastest.
// Apify Actor self-timeout di-config di Actor input (e.g. 600s untuk web).
export const TIMEOUT_SECONDS = {
  instagram: 90,
  tiktok:    90,
  facebook:  90,
  linkedin:  120,
  threads:   60,
  reddit:    60,
  x:         60,
  twitter:   60,
  youtube:   60,
  web:       30,
  news:      60,        // bumped 30→60 untuk dedicated easyapi actor
  content:   120,
  maps:      30,
};

// ============================================================================
// RETRY CONFIG — Per actor risk level
// ============================================================================
// Actors yang sering 0-result OR rate-limit → retry dengan interval.
// X tweet-scraper paling tidak stabil (X API rotates guest tokens).
// LinkedIn cookie-based — rotate ke akun lain pool jika 999.
// TikTok signature change ~bulanan → tunggu 30 menit.
// ============================================================================
export const RETRY_CONFIG = {
  x: {
    max_retries:    2,
    interval_ms:    300000,     // 5 menit
    trigger:        'zero_results_or_error',
    fallback_actor: ACTOR_FALLBACKS.x,
  },
  twitter: {
    max_retries:    2,
    interval_ms:    300000,
    trigger:        'zero_results_or_error',
    fallback_actor: ACTOR_FALLBACKS.x,
  },
  linkedin: {
    max_retries:    1,
    interval_ms:    0,          // immediate retry, rotate cookie
    trigger:        'http_999_or_cookie_expired',
    fallback_actor: ACTOR_FALLBACKS.linkedin,
  },
  threads: {
    max_retries:    1,
    interval_ms:    0,
    trigger:        'api_endpoint_error',
    fallback_actor: ACTOR_FALLBACKS.threads,  // browser-based
  },
  tiktok: {
    max_retries:    1,                  // turun 2→1 (sebelumnya retry storm bikin badge DEGRADED)
    interval_ms:    0,                  // immediate, gunakan fallback bukan retry primary
    trigger:        'http_400_or_error',
    fallback_actor: ACTOR_FALLBACKS.tiktok,  // clockworks~tiktok-profile-scraper
  },
  facebook: {
    max_retries:    1,
    interval_ms:    0,
    trigger:        'http_400_or_error',
    fallback_actor: ACTOR_FALLBACKS.facebook,  // apify~facebook-pages-scraper
  },
  news: {
    max_retries:    1,
    interval_ms:    0,
    trigger:        'http_400_or_billing_error',
    fallback_actor: ACTOR_FALLBACKS.news,      // apify~google-search-scraper
  },
  default: {
    max_retries:    1,
    interval_ms:    0,
    trigger:        'failed_status',
    fallback_actor: null,
  },
};

// ============================================================================
// RISK LEVELS — informational, used by UI for status indicators
// ============================================================================
export const RISK_LEVELS = {
  LOW:    ['web', 'news', 'youtube', 'content', 'maps'],
  MEDIUM: ['instagram', 'tiktok', 'threads', 'facebook'],
  HIGH:   ['x', 'twitter', 'linkedin'],
};

// ============================================================================
// Brand24 — Netcomm The Radar Native (DISABLED v1)
// ============================================================================
export const BRAND24_CONFIG = {
  status:        E.VITE_BRAND24_ENABLED === 'true' ? 'ENABLED' : 'DISABLED',
  label_id:      'Netcomm The Radar Native',
  label_en:      'Netcomm The Radar Native',
  tooltip_id:    'Netcomm The Radar Native tersedia di versi mendatang — monitoring real-time berkelanjutan. Hubungi admin untuk aktivasi.',
  tooltip_en:    'Netcomm The Radar Native available in future versions — continuous real-time monitoring. Contact admin for activation.',
  activation:    'Set BRAND24_ENABLED=true di environment — tidak perlu code change',
};

// ============================================================================
// HELPERS
// ============================================================================

// Lookup single actor (backward-compat — return first if array)
export function getStage1Actor(platform) {
  const v = STAGE1_ACTOR_MAP[platform];
  if (!v) return null;
  return Array.isArray(v) ? v[0] : v;
}

export function getStage2Actor(platform) {
  const v = STAGE2_ACTOR_MAP[platform];
  if (!v) return null;
  return Array.isArray(v) ? v[0] : v;
}

// Multi-actor lookup (return array — single wrapped, multi as-is)
export function getStage1Actors(platform) {
  const v = STAGE1_ACTOR_MAP[platform];
  if (!v) return [];
  return Array.isArray(v) ? v : [v];
}

export function getStage2Actors(platform) {
  const v = STAGE2_ACTOR_MAP[platform];
  if (!v) return [];
  return Array.isArray(v) ? v : [v];
}

export function getProxyConfig(platform) {
  return PROXY_CONFIG[platform] || { group: 'auto', country: null, sticky: false };
}

export function getTimeoutMs(platform) {
  return (TIMEOUT_SECONDS[platform] || 60) * 1000;
}

export function getRetryConfig(platform) {
  return RETRY_CONFIG[platform] || RETRY_CONFIG.default;
}

export function getRiskLevel(platform) {
  for (const [level, list] of Object.entries(RISK_LEVELS)) {
    if (list.includes(platform)) return level;
  }
  return 'UNKNOWN';
}

export function getFallbackActor(platform) {
  return ACTOR_FALLBACKS[platform] || null;
}
